import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, AlertCircle, CheckCircle, Loader } from 'lucide-react';

// Contact form data type for Consciousness Exploration inquiries
interface ConsciousnessContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
  experienceLevel: string;
  interests: string[];
  message: string;
  consent: boolean;
}

// Apollo response type
interface ApolloResponse {
  success: boolean;
  message: string;
}

export default function ConsciousnessContactForm() {
  const [formData, setFormData] = useState<ConsciousnessContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    experienceLevel: '',
    interests: [],
    message: '',
    consent: false
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ConsciousnessContactFormData, string>>>({});
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [responseMessage, setResponseMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } 
    // Handle multi-select interests
    else if (name === 'interests') {
      const select = e.target as HTMLSelectElement;
      const selectedOptions = Array.from(select.selectedOptions).map(option => option.value);
      setFormData(prev => ({ ...prev, interests: selectedOptions }));
    }
    // Handle all other inputs
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is edited
    if (errors[name as keyof ConsciousnessContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ConsciousnessContactFormData, string>> = {};
    
    // Required fields validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.interests.length === 0) newErrors.interests = 'Please select at least one area of interest';
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Consent validation
    if (!formData.consent) newErrors.consent = 'Please consent to our privacy policy';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmissionStatus('submitting');
    
    try {
      // Get Apollo API key from env
      const apiKey = import.meta.env.VITE_APOLLO_API_KEY;
      
      // Prepare data for Apollo
      const apolloData = {
        api_key: apiKey,
        person: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          organization_name: formData.organization || 'Individual'
        },
        add_person_to_sequence: false,
        additional_data: {
          experience_level: formData.experienceLevel,
          areas_of_interest: formData.interests.join(', '),
          inquiry_details: formData.message
        },
        labels: ["Consciousness Exploration Inquiry", "Neuaura Program"],
        tags: ["Monroe Institute", "Consciousness Research", "Neural Interface"],
        source_details: "Consciousness Exploration Case Study Inquiry Form"
      };
      
      // In development mode, simulate the API call
      if (process.env.NODE_ENV === 'development') {
        console.log('Development mode - Simulating Apollo API call with data:', apolloData);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Simulate success
        setSubmissionStatus('success');
        setResponseMessage('Thank you for your interest in our Neuaura consciousness exploration program! Our team will contact you within 24 hours to discuss how you can begin your journey of expanded awareness.');
      } else {
        // In production, make the actual API call
        const response = await fetch('https://api.apollo.io/v1/contacts/add_or_update', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apolloData)
        });
        
        const data: ApolloResponse = await response.json();
        
        if (data.success) {
          setSubmissionStatus('success');
          setResponseMessage('Thank you for your interest in our Neuaura consciousness exploration program! Our team will contact you within 24 hours to discuss how you can begin your journey of expanded awareness.');
        } else {
          throw new Error(data.message || 'Failed to submit form');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmissionStatus('error');
      setResponseMessage('We encountered an error while submitting your form. Please try again later or contact us directly.');
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      {submissionStatus === 'success' ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-900/20 border border-green-500/30 rounded-lg p-6 text-center"
        >
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">
            Inquiry Received!
          </h3>
          <p className="text-green-200/80">
            {responseMessage}
          </p>
        </motion.div>
      ) : (
        <motion.form 
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-white mb-2">
              Neuaura Program Inquiry
            </h3>
            <p className="text-gray-400">
              Interested in exploring consciousness through our holistic Neuaura program? Fill out this form and we'll get back to you with details!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                First Name*
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-2 bg-gray-800 border ${
                  errors.firstName ? 'border-red-500' : 'border-gray-700'
                } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]`}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                Last Name*
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-2 bg-gray-800 border ${
                  errors.lastName ? 'border-red-500' : 'border-gray-700'
                } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]`}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
              Email Address*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-gray-800 border ${
                errors.email ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Organization */}
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-1">
              Organization (if applicable)
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              placeholder="Company, research group, or institution"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]"
            />
          </div>

          {/* Experience Level */}
          <div>
            <label htmlFor="experienceLevel" className="block text-sm font-medium text-gray-300 mb-1">
              Experience Level with Meditation/Consciousness Practices
            </label>
            <select
              id="experienceLevel"
              name="experienceLevel"
              value={formData.experienceLevel}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]"
            >
              <option value="">Select Your Experience Level</option>
              <option value="Beginner">Beginner - Little to no experience</option>
              <option value="Intermediate">Intermediate - Some regular practice</option>
              <option value="Advanced">Advanced - Regular practice for 1+ years</option>
              <option value="Expert">Expert - Experienced practitioner/teacher</option>
            </select>
          </div>

          {/* Areas of Interest */}
          <div>
            <label htmlFor="interests" className="block text-sm font-medium text-gray-300 mb-1">
              Areas of Interest* (select multiple)
            </label>
            <select
              id="interests"
              name="interests"
              multiple
              value={formData.interests}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-gray-800 border ${
                errors.interests ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2] h-32`}
            >
              <option value="Physical Training">Physical Training Integration</option>
              <option value="Meditation Techniques">Advanced Meditation Techniques</option>
              <option value="Monroe Focus Levels">Monroe Institute Focus Levels</option>
              <option value="Mudras and Energy Work">Mudras and Energy Work</option>
              <option value="Neural Interface Applications">Neural Interface Applications</option>
              <option value="Psionic Abilities">Psionic Abilities Development</option>
              <option value="Consciousness Research">Consciousness Research Participation</option>
              <option value="Corporate Wellness">Corporate Wellness Programs</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple options</p>
            {errors.interests && (
              <p className="mt-1 text-sm text-red-500">{errors.interests}</p>
            )}
          </div>

          {/* Message/Details */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
              Tell us about your goals and interests with consciousness exploration*
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="What aspects of consciousness exploration interest you most? Do you have specific goals? Any previous experiences with meditation or similar practices?"
              className={`w-full px-4 py-2 bg-gray-800 border ${
                errors.message ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#8A2BE2]`}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-500">{errors.message}</p>
            )}
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="consent"
                name="consent"
                type="checkbox"
                checked={formData.consent}
                onChange={handleChange}
                className="h-4 w-4 text-[#8A2BE2] focus:ring-[#8A2BE2] border-gray-600 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="consent" className={`text-sm ${errors.consent ? 'text-red-500' : 'text-gray-400'}`}>
                I consent to SELF Labs storing and processing my personal data in accordance with the Privacy Policy*
              </label>
            </div>
          </div>

          {/* Error Message */}
          {submissionStatus === 'error' && (
            <div className="flex items-center p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-200">
                {responseMessage}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submissionStatus === 'submitting'}
            className="w-full bg-gradient-to-r from-[#8A2BE2] to-[#4B0082] hover:from-[#9B4DFF] hover:to-[#5D0099] text-white font-semibold py-3 rounded-lg flex justify-center items-center transition-all duration-300"
          >
            {submissionStatus === 'submitting' ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Inquiry
              </>
            )}
          </button>
        </motion.form>
      )}
    </div>
  );
} 